/* eslint-disable react/no-array-index-key */
import { useCallback } from 'react';

import styled from 'styled-components';

import { FormFields } from './AgentsListBody.decl';

import { EditOutlined } from '@aircall/icons';
import { Box, Flex, IconButton, Tooltip } from '@aircall/tractor-v2';
import { AgentRolesPicker } from '@components/AgentRolesPicker/AgentRolesPicker';
import { ModalForm } from '@components/ModalFormV2';
import { RolesTags } from '@components/RolesTags';
import { UserMinimalFragment } from '@generated/UserMinimalFragment';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useSetRoles } from '@hooks/useSetRoles';
import { useToggle } from '@hooks/useToggle/useToggle';
import debounce from 'lodash-es/debounce';
import { useTranslation } from 'react-i18next';

const CustomIconButton = styled(IconButton)`
  > div {
    z-index: unset;
  }

  &::before {
    z-index: unset;
  }
`;

export function ColumnFieldRole({ ID: userId, roles, firstName, lastName }: UserMinimalFragment) {
  const { t } = useTranslation();
  const { setRoles } = useSetRoles();
  const {
    currentUser: { ID: currentUserId },
  } = useGlobalData();
  const [isOpen, _, toggleOpen, hide] = useToggle();

  const isOwnUser = userId === currentUserId;

  const handleSubmit = useCallback(
    async (input: FormFields) => {
      await setRoles({ roles: input.roles, userId: userId.toString() });

      debounce(hide, 3000)();
      return undefined;
    },
    [setRoles, hide, userId]
  );

  return (
    <Flex alignItems='center' onClick={(e) => e.stopPropagation()}>
      <RolesTags roles={roles} />
      <Tooltip title={t('users.table.role.button_tooltip')} position='top'>
        <CustomIconButton
          color='neutral-600'
          onClick={toggleOpen}
          ml='8px'
          component={EditOutlined}
          size='s'
          data-test='user-role-edit-button'
          disabled={isOwnUser}
          discColor='primary-100'
        />
      </Tooltip>
      <ModalForm
        size='small'
        show={isOpen}
        title={`${firstName} ${lastName}`}
        onHide={hide}
        submitButtonText={t('update_role_modal.update_user')}
        onCancelClick={hide}
        rootClose
        formProps={{
          onSubmit: handleSubmit,
          initialValues: { roles },
        }}
      >
        {({ submitting }) => (
          <Box padding='m'>
            <AgentRolesPicker disabled={submitting} name='roles' showInfoBanner />
          </Box>
        )}
      </ModalForm>
    </Flex>
  );
}
